import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFetch } from "@/hooks/useFetch";
import { BOOK_STATUS, WEEKDAYS, fmtTime, isAllDay, todayZurich } from "@/lib/format";
import { Card, PageHeader, Sel, mapOpts } from "@/components/Field";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
const TONE = { pending: "bg-amber-100 text-amber-900 border-amber-300", confirmed: "bg-green-100 text-green-900 border-green-300", cancelled: "bg-slate-100 text-slate-500 line-through border-slate-200", completed: "bg-blue-50 text-blue-900 border-blue-200", event: "bg-primary/10 text-primary border-primary/30" };

function range(view, cursor) {
  if (view === "day") return [cursor, cursor];
  if (view === "week") { const m = mondayOf(cursor); return [m, addDays(m, 6)]; }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = mondayOf(first);
  return [start, addDays(start, 41)];
}

const Item = ({ it, onClick }) => (
  <button data-testid={`cal-item-${it.kind}-${it.id}`} onClick={onClick} className={`w-full text-left truncate rounded border px-1.5 py-0.5 text-xs font-medium ${TONE[it.kind === "event" ? "event" : it.status]}`}>
    {isAllDay(it) ? "" : fmtTime(it.start)} {it.title}
  </button>
);

export default function CalendarPage() {
  const nav = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date(`${todayZurich()}T00:00`));
  const [room, setRoom] = useState("");
  const [status, setStatus] = useState("");
  const [from, to] = range(view, cursor);
  const rooms = useFetch("/rooms").data || [];
  const { data } = useFetch(`/calendar?date_from=${iso(from)}&date_to=${iso(to)}${room ? `&room_id=${room}` : ""}`);
  const items = (data?.items || []).filter((i) => !status || (i.kind === "booking" && i.status === status));
  const closures = useMemo(() => (data?.closures || []).filter((c) => !room || c.room_id === room), [data, room]);
  const days = Array.from({ length: Math.round((to - from) / 86400000) + 1 }, (_, i) => addDays(from, i));
  const today = todayZurich();
  const step = (n) => setCursor(view === "month" ? new Date(cursor.getFullYear(), cursor.getMonth() + n, 1) : addDays(cursor, n * (view === "week" ? 7 : 1)));
  const open = (it) => nav(it.kind === "event" ? `/eventi/${it.id}` : "/sale");
  const forDay = (d) => items.filter((i) => i.start.slice(0, 10) <= iso(d) && i.end.slice(0, 10) >= iso(d));
  const closedOn = (d) => room && closures.some((c) => c.closed_weekdays.includes((d.getDay() + 6) % 7));
  const title = view === "month" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}` : view === "week" ? `Settimana dal ${from.getDate()}.${from.getMonth() + 1}.` : `${WEEKDAYS[(cursor.getDay() + 6) % 7]} ${cursor.getDate()} ${MONTHS[cursor.getMonth()]}`;

  return (
    <>
      <PageHeader title="Calendario" subtitle="Prenotazioni delle sale ed eventi" />
      <Card className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="icon" className="h-11 w-11" onClick={() => step(-1)}><ChevronLeft className="h-5 w-5" /></Button>
          <Button data-testid="cal-today" variant="outline" className="h-11" onClick={() => setCursor(new Date(`${today}T00:00`))}>Oggi</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" className="h-11 w-11" onClick={() => step(1)}><ChevronRight className="h-5 w-5" /></Button>
          <span data-testid="cal-title" className="font-heading font-semibold text-lg ml-2">{title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={view} onValueChange={setView}><TabsList className="h-11">
            <TabsTrigger value="month" className="h-9" data-testid="cal-view-month">Mese</TabsTrigger>
            <TabsTrigger value="week" className="h-9" data-testid="cal-view-week">Settimana</TabsTrigger>
            <TabsTrigger value="day" className="h-9" data-testid="cal-view-day">Giorno</TabsTrigger>
          </TabsList></Tabs>
          <div className="w-44"><Sel testId="cal-filter-room" value={room} onChange={setRoom} placeholder="Tutte le sale" options={rooms.map((r) => ({ value: r.id, label: r.name }))} /></div>
          <div className="w-40"><Sel testId="cal-filter-status" value={status} onChange={setStatus} placeholder="Tutti gli stati" options={mapOpts(BOOK_STATUS).filter((o) => o.value !== "rejected")} /></div>
        </div>
      </Card>

      {view === "month" ? (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-50 text-xs font-semibold text-slate-600">{WEEKDAYS.map((d) => <div key={d} className="px-2 py-2">{d.slice(0, 3)}</div>)}</div>
          <div className="grid grid-cols-7" data-testid="cal-month-grid">
            {days.map((d) => (
              <div key={iso(d)} className={`min-h-[96px] border-t border-l p-1 space-y-1 ${d.getMonth() !== cursor.getMonth() ? "bg-slate-50/60 text-slate-400" : ""} ${closedOn(d) ? "bg-slate-100" : ""}`}>
                <button onClick={() => { setCursor(d); setView("day"); }} className={`text-xs font-semibold h-6 w-6 rounded-full ${iso(d) === today ? "bg-primary text-primary-foreground" : ""}`}>{d.getDate()}</button>
                {closedOn(d) && <div className="text-[10px] text-slate-500">Chiusa</div>}
                {forDay(d).slice(0, 3).map((it) => <Item key={it.kind + it.id} it={it} onClick={() => open(it)} />)}
                {forDay(d).length > 3 && <div className="text-[10px] text-slate-500">+{forDay(d).length - 3} altri</div>}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <div className={`grid gap-3 ${view === "week" ? "grid-cols-1 md:grid-cols-7" : "grid-cols-1"}`}>
          {days.map((d) => (
            <Card key={iso(d)} className={`p-3 space-y-2 ${closedOn(d) ? "bg-slate-100" : ""}`}>
              <div className={`text-sm font-semibold ${iso(d) === today ? "text-primary" : "text-slate-700"}`}>{WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}.{d.getMonth() + 1}.</div>
              {closedOn(d) && <div className="text-xs text-slate-500">Sala chiusa</div>}
              {forDay(d).length === 0 && !closedOn(d) && <div className="text-xs text-slate-400">Libero</div>}
              {forDay(d).map((it) => (
                <button key={it.kind + it.id} onClick={() => open(it)} className={`w-full text-left rounded-md border px-2 py-1.5 text-sm ${TONE[it.kind === "event" ? "event" : it.status]}`}>
                  <div className="font-semibold">{isAllDay(it) ? "Giornata intera" : `${fmtTime(it.start)}–${fmtTime(it.end)}`}</div><div>{it.title}</div>
                  <div className="text-xs opacity-80">{it.kind === "event" ? "Evento" : BOOK_STATUS[it.status]?.label}</div>
                </button>
              ))}
            </Card>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3 text-xs">{[["pending", "In attesa"], ["confirmed", "Confermata"], ["cancelled", "Cancellata"], ["completed", "Completata"], ["event", "Evento"]].map(([k, l]) => <span key={k} className={`rounded border px-2 py-1 ${TONE[k]}`}>{l}</span>)}</div>
    </>
  );
}
